import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { reRunTask, type AgentTask } from './api';

// ManualTaskPanel is the inline expansion under a "Run now" task row.
// The task already fired once at create time; this panel lets the
// operator re-trigger it without rewriting the objective.
export function ManualTaskPanel({
  orgId,
  agentId,
  task,
}: {
  orgId: string;
  agentId: string;
  task: AgentTask;
}) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const rerun = useMutation({
    mutationFn: () => reRunTask(orgId, agentId, task.id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['agent-tasks', orgId, agentId] });
      qc.invalidateQueries({ queryKey: ['runs', orgId, agentId] });
      nav(`/agents/${agentId}/runs/${res.run_id}`);
    },
  });

  return (
    <div className="mt-2 rounded-lg border border-line-soft bg-surface-soft/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
            Objective
          </p>
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-primary">
            {task.objective}
          </p>
        </div>
        <Button onClick={() => rerun.mutate()} disabled={rerun.isPending}>
          {rerun.isPending ? 'Running…' : 'Re-run'}
        </Button>
      </div>
      {rerun.error && (
        <p className="mt-2 text-xs text-danger">
          {(rerun.error as Error).message || 'Could not re-run'}
        </p>
      )}
    </div>
  );
}
